import { useState } from 'react';
import {
  AlertCircle,
  CheckCircle2,
  Download,
  FileSpreadsheet,
  Loader2,
  Upload,
  X,
} from 'lucide-react';
import { api, extractErrorMessage } from '../../lib/api';
import { ModalPortal } from '../ModalPortal';

/**
 * Modal d'import Excel des services (US-TR-01 / US-SVC-01).
 *
 *  - Téléchargement du modèle Excel (1 feuille)
 *  - Upload .xlsx → POST `/services/import`
 *  - Rapport : { total, created, updated, failed, errors[] }
 *
 * Mode upsert par code. La colonne `service_parent_code` est résolue par code
 * (lignes en erreur si parent introuvable).
 */

interface ImportReport {
  total: number;
  created: number;
  updated: number;
  failed: number;
  errors: Array<{ row: number; code: string; message: string }>;
}

export function ImportServicesModal({
  onClose,
  onCompleted,
}: {
  onClose(): void;
  onCompleted(): void;
}) {
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [report, setReport] = useState<ImportReport | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  async function downloadTemplate() {
    try {
      const res = await api.get('/services/import-template.xlsx', {
        responseType: 'blob',
      });
      const blob = new Blob([res.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'services-template.xlsx';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      setErrorMessage(extractErrorMessage(err));
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!file) {
      setErrorMessage('Sélectionnez un fichier .xlsx');
      return;
    }
    setUploading(true);
    setErrorMessage(null);
    setReport(null);
    try {
      const form = new FormData();
      form.append('file', file);
      const res = await api.post<{ data: ImportReport }>('/services/import', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setReport(res.data.data);
    } catch (err) {
      setErrorMessage(extractErrorMessage(err));
    } finally {
      setUploading(false);
    }
  }

  return (
    <ModalPortal>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl max-w-2xl w-full shadow-2xl max-h-[90vh] flex flex-col">
          <div className="flex items-center justify-between p-4 border-b border-gray-200 flex-shrink-0">
            <h2 className="text-lg font-semibold text-gray-900 flex items-center">
              <FileSpreadsheet className="w-5 h-5 mr-2 text-primary-600" />
              Importer des services
            </h2>
            <button
              onClick={onClose}
              className="p-1 rounded-lg hover:bg-gray-100"
              aria-label="Fermer"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {errorMessage && (
              <div className="rounded-lg border border-red-200 bg-red-50 p-3 flex items-start space-x-2">
                <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-700">{errorMessage}</p>
              </div>
            )}

            {!report ? (
              <form id="import-services-form" onSubmit={handleSubmit} className="space-y-4">
                <div className="rounded-lg bg-blue-50 border border-blue-200 p-3 text-sm text-blue-900">
                  <p className="font-medium mb-1">Mode upsert par code</p>
                  <p className="text-xs mb-2">
                    Les codes existants sont mis à jour, les autres créés. La colonne{' '}
                    <code className="font-mono">service_parent_code</code> est résolue par code dans
                    le tenant — les lignes en erreur si le parent est introuvable.
                  </p>
                  <button
                    type="button"
                    onClick={downloadTemplate}
                    className="inline-flex items-center text-xs font-medium text-primary-600 hover:text-primary-700"
                  >
                    <Download className="w-3.5 h-3.5 mr-1" />
                    Télécharger le modèle Excel
                  </button>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Fichier Excel
                  </label>
                  <input
                    type="file"
                    accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                    onChange={(e) => setFile(e.target.files?.[0] ?? null)}
                    className="block w-full text-sm text-gray-700 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-primary-50 file:text-primary-700 hover:file:bg-primary-100"
                  />
                  {file && (
                    <p className="text-xs text-gray-500 mt-1">
                      {file.name} · {(file.size / 1024).toFixed(1)} ko
                    </p>
                  )}
                </div>
              </form>
            ) : (
              <ReportView report={report} />
            )}
          </div>

          <div className="flex items-center justify-end space-x-2 p-4 border-t border-gray-200 flex-shrink-0">
            {report ? (
              <button
                onClick={onCompleted}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-primary-500 hover:bg-primary-600"
              >
                Fermer et rafraîchir
              </button>
            ) : (
              <>
                <button
                  onClick={onClose}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  form="import-services-form"
                  disabled={uploading || !file}
                  className="inline-flex items-center px-4 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-primary-500 hover:bg-primary-600 disabled:opacity-50"
                >
                  {uploading ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Upload className="w-4 h-4 mr-2" />
                  )}
                  Lancer l'import
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </ModalPortal>
  );
}

function ReportView({ report }: { report: ImportReport }) {
  const ok = report.failed === 0;
  return (
    <div className="space-y-4">
      <div
        className={`rounded-lg border p-3 flex items-start space-x-2 ${
          ok
            ? 'border-green-200 bg-green-50 text-green-800'
            : 'border-amber-200 bg-amber-50 text-amber-800'
        }`}
      >
        {ok ? (
          <CheckCircle2 className="w-5 h-5 flex-shrink-0 mt-0.5" />
        ) : (
          <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
        )}
        <div className="text-sm">
          <p className="font-medium">
            {ok ? 'Import terminé avec succès' : 'Import terminé avec des erreurs'}
          </p>
          <p className="text-xs mt-1">{report.total} ligne(s) traitée(s).</p>
        </div>
      </div>

      <div className="grid grid-cols-4 gap-3 text-center">
        <Stat label="Total" value={report.total} tone="default" />
        <Stat label="Créés" value={report.created} tone="green" />
        <Stat label="Mis à jour" value={report.updated} tone="blue" />
        <Stat label="Erreurs" value={report.failed} tone="red" />
      </div>

      {report.errors.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold text-gray-900 mb-2">
            Erreurs ({report.errors.length})
          </h3>
          <div className="rounded-lg border border-gray-200 max-h-64 overflow-y-auto">
            <table className="min-w-full divide-y divide-gray-200 text-xs">
              <thead className="bg-gray-50 sticky top-0">
                <tr>
                  <th className="px-3 py-2 text-left font-medium text-gray-500 uppercase">Ligne</th>
                  <th className="px-3 py-2 text-left font-medium text-gray-500 uppercase">Code</th>
                  <th className="px-3 py-2 text-left font-medium text-gray-500 uppercase">
                    Message
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {report.errors.map((e, i) => (
                  <tr key={i}>
                    <td className="px-3 py-2 text-gray-700">{e.row}</td>
                    <td className="px-3 py-2 text-gray-700 font-mono">{e.code}</td>
                    <td className="px-3 py-2 text-red-700">{e.message}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function Stat({
  label,
  value,
  tone,
}: {
  label: string;
  value: number;
  tone: 'default' | 'green' | 'blue' | 'red';
}) {
  const tones: Record<typeof tone, string> = {
    default: 'text-gray-900',
    green: 'text-green-700',
    blue: 'text-blue-700',
    red: 'text-red-700',
  };
  return (
    <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
      <p className="text-xs uppercase tracking-wide text-gray-500">{label}</p>
      <p className={`text-xl font-bold mt-1 ${tones[tone]}`}>{value}</p>
    </div>
  );
}
